'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketId } from '@morpho-org/blue-sdk';
import type { InterfaceToken } from '@morpho-org/dapp-core';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { CompactAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { ActionButtons } from '@/components/common/ActionButtons';
import BundleAuthorizationCallout from '@/components/common/BundleAuthorizationCallout/BundleAuthorizationCallout';
import { MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import { TransferError } from '@/components/common/Error/TransferErrors';
import { POSITION_MANAGER_DIALOG_OPTIONS } from '@/components/common/PositionManager/dialogOptions';
import { WrapSegment } from '@/components/common/WrapSegment';
import MarketActionReviewDialogV2 from '@/components/MarketPage/MarketActionV2/MarketActionReviewDialogV2';
import { MarketSummaryCard } from '@/components/MarketPage/MarketSimpleActionPanel/MarketSimpleActionPanelClient/MarketOperationPanel/MarketSummary';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getMarketGeoRestrictionActionProps } from '@/helpers/marketGeoRestriction';
import { getRepayWithdrawOperationLabel } from '@/helpers/operationLabels';
import useMarketTooltip from '@/hooks/operation/market/useMarketTooltip';
import { computeLtv, computePreviewFinalPosition } from '@/hooks/operation/market/v2/marketAction.helpers';
import { getMarketActionError } from '@/hooks/operation/market/v2/marketActionError';
import { useMarketActionBuilder } from '@/hooks/operation/market/v2/useMarketActionBuilder';
import { useMarketBlue } from '@/hooks/operation/market/v2/useMarketBlue';
import { useMarketMaxWithdrawCollateralV2 } from '@/hooks/operation/market/v2/useMarketMaxWithdrawCollateralV2';
import { useMarketRepayNativeWrap } from '@/hooks/operation/market/v2/useMarketRepayNativeWrap';
import usePositionManagerMarketDialogLabels from '@/hooks/operation/position-manager/usePositionManagerMarketDialogLabels';
import useAssetCanTransferOnchain from '@/hooks/operation/useAssetCanTransferOnchain';
import useBundleCanTransferOnchain, { isMarketBundleBlocked } from '@/hooks/operation/useBundleCanTransferOnchain';
import { useMarketGeoRestriction } from '@/hooks/useMarketGeoRestriction';
import { useTrackMarketActionsV2EntryViewed } from '@/lib/analytics/client/hooks/useTrackMarketActionsV2EntryViewed';
import { trackMarketActionsV2EntryClicked } from '@/lib/analytics/client/trackers/market-actions-v2';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { converter } from '@/selectors/converter';
import type { RepayDialogMarket } from '../toRepayDialogMarket';
import PositionManagerRepayWithBalanceLoading from './states/PositionManagerRepayWithBalanceLoading';

/** Debounce window (ms) before an edited amount drives the local build (validation only). */
const PREVIEW_DEBOUNCE_MS = 400;
interface PositionManagerRepayWithBalanceProps {
  marketId: MarketId;
  isQueryLoading: boolean;
  queryError?: Error | null;
  loanAsset?: InterfaceToken;
  collateralAsset?: InterfaceToken;
  market?: RepayDialogMarket;
}
const PositionManagerRepayWithBalance = t0 => {
  const $ = _c(182);
  const {
    marketId,
    loanAsset,
    collateralAsset,
    market,
    queryError,
    isQueryLoading
  } = t0;
  const {
    account,
    targetChain
  } = useWeb3Context();
  const {
    toggleDialog
  } = useDialogContext();
  const {
    marketRepayWithdraw
  } = useFeatureFlags();
  const [inputCollateralAmount, setInputCollateralAmount] = useState(ZERO);
  const [inputLoanAmount, setInputLoanAmount] = useState(ZERO);
  const [isCollateralInputPending, setIsCollateralInputPending] = useState(false);
  const [isLoanInputPending, setIsLoanInputPending] = useState(false);
  let t1;
  if ($[0] !== market) {
    t1 = market ? converter.getMarketParams(market) : undefined;
    $[0] = market;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const marketParams = t1;
  const isMarketRepayWithdrawEnabled = marketRepayWithdraw && !!marketParams;
  const t2 = market?.collateralAsset?.address;
  const t3 = market?.loanAsset.address;
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = [t2, t3];
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4 || $[6] !== targetChain.id) {
    t5 = {
      chainId: targetChain.id,
      assetAddresses: t4
    };
    $[5] = t4;
    $[6] = targetChain.id;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const geoRestriction = useMarketGeoRestriction(t5);
  let t6;
  if ($[8] !== isMarketRepayWithdrawEnabled || $[9] !== marketId || $[10] !== targetChain.id || $[11] !== targetChain.name) {
    t6 = {
      entryPoint: "position_manager",
      action: "repay_withdraw",
      marketId,
      chainId: targetChain.id,
      chainName: targetChain.name,
      enabled: isMarketRepayWithdrawEnabled
    };
    $[8] = isMarketRepayWithdrawEnabled;
    $[9] = marketId;
    $[10] = targetChain.id;
    $[11] = targetChain.name;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  useTrackMarketActionsV2EntryViewed(t6);
  const {
    positionData,
    error: positionError,
    isLoading: isPositionLoading
  } = useMarketBlue(marketParams);
  const debt = positionData?.borrowAssets;
  let t7;
  if ($[13] !== debt || $[14] !== inputLoanAmount || $[15] !== isMarketRepayWithdrawEnabled) {
    t7 = isMarketRepayWithdrawEnabled && isDefined(debt) && debt > ZERO && inputLoanAmount >= debt;
    $[13] = debt;
    $[14] = inputLoanAmount;
    $[15] = isMarketRepayWithdrawEnabled;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const isMaxRepay = t7;
  const t8 = loanAsset?.address;
  let t9;
  if ($[17] !== inputLoanAmount || $[18] !== isMarketRepayWithdrawEnabled || $[19] !== isMaxRepay || $[20] !== marketParams || $[21] !== t8) {
    t9 = {
      marketParams,
      loanToken: t8,
      totalRepay: inputLoanAmount,
      isMaxRepay,
      enabled: isMarketRepayWithdrawEnabled,
      wrapEnabled: isMarketRepayWithdrawEnabled
    };
    $[17] = inputLoanAmount;
    $[18] = isMarketRepayWithdrawEnabled;
    $[19] = isMaxRepay;
    $[20] = marketParams;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const {
    isWNativeLoan,
    isWrapping,
    onWrappingChange,
    wrappableNativeBalance,
    nativeAmount,
    maxRepay,
    isMaxLoading: isMaxRepayLoading,
    isWrappingToggleLoading,
    wrapErrorMessage
  } = useMarketRepayNativeWrap(t9);
  let t10;
  if ($[23] !== inputLoanAmount || $[24] !== isMarketRepayWithdrawEnabled || $[25] !== maxRepay) {
    t10 = isMarketRepayWithdrawEnabled && isDefined(maxRepay) && maxRepay > ZERO && inputLoanAmount === maxRepay;
    $[23] = inputLoanAmount;
    $[24] = isMarketRepayWithdrawEnabled;
    $[25] = maxRepay;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const isRepayAtMax = t10;
  let t11;
  if ($[27] !== inputLoanAmount || $[28] !== isMarketRepayWithdrawEnabled || $[29] !== maxRepay) {
    t11 = isMarketRepayWithdrawEnabled && isDefined(maxRepay) && inputLoanAmount > maxRepay;
    $[27] = inputLoanAmount;
    $[28] = isMarketRepayWithdrawEnabled;
    $[29] = maxRepay;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const isRepayOverMax = t11;
  let t12;
  if ($[31] !== maxRepay) {
    t12 = value => {
      if (value && isDefined(maxRepay)) {
        setInputLoanAmount(maxRepay);
      }
    };
    $[31] = maxRepay;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const onMaxRepayClick = t12;
  let t13;
  if ($[33] !== inputLoanAmount || $[34] !== isMarketRepayWithdrawEnabled || $[35] !== isMaxRepay || $[36] !== marketParams) {
    t13 = {
      marketParams,
      proposedLoanRepay: inputLoanAmount,
      isMaxRepay,
      enabled: isMarketRepayWithdrawEnabled
    };
    $[33] = inputLoanAmount;
    $[34] = isMarketRepayWithdrawEnabled;
    $[35] = isMaxRepay;
    $[36] = marketParams;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const {
    maxWithdraw,
    maxWithdrawCeiling,
    isMaxLoading: isMaxWithdrawLoading
  } = useMarketMaxWithdrawCollateralV2(t13);
  let t14;
  if ($[38] !== maxWithdraw) {
    t14 = value_0 => {
      if (value_0 && isDefined(maxWithdraw)) {
        setInputCollateralAmount(maxWithdraw);
      }
    };
    $[38] = maxWithdraw;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const onMaxWithdrawClick = t14;
  let t15;
  if ($[40] !== inputCollateralAmount || $[41] !== isMarketRepayWithdrawEnabled || $[42] !== maxWithdraw) {
    t15 = isMarketRepayWithdrawEnabled && isDefined(maxWithdraw) && maxWithdraw > ZERO && inputCollateralAmount === maxWithdraw;
    $[40] = inputCollateralAmount;
    $[41] = isMarketRepayWithdrawEnabled;
    $[42] = maxWithdraw;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const isMaxWithdraw = t15;
  let t16;
  if ($[44] !== inputCollateralAmount || $[45] !== isMarketRepayWithdrawEnabled || $[46] !== maxWithdrawCeiling) {
    t16 = isMarketRepayWithdrawEnabled && isDefined(maxWithdrawCeiling) && inputCollateralAmount > maxWithdrawCeiling;
    $[44] = inputCollateralAmount;
    $[45] = isMarketRepayWithdrawEnabled;
    $[46] = maxWithdrawCeiling;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  const isWithdrawOverMax = t16;
  const [debouncedCollateral] = useDebounce(inputCollateralAmount, PREVIEW_DEBOUNCE_MS);
  const [debouncedLoan] = useDebounce(inputLoanAmount, PREVIEW_DEBOUNCE_MS);
  const isDebouncing = debouncedCollateral !== inputCollateralAmount || debouncedLoan !== inputLoanAmount;
  let t17;
  if ($[48] !== debouncedLoan || $[49] !== debt || $[50] !== isMarketRepayWithdrawEnabled) {
    t17 = isMarketRepayWithdrawEnabled && isDefined(debt) && debt > ZERO && debouncedLoan >= debt;
    $[48] = debouncedLoan;
    $[49] = debt;
    $[50] = isMarketRepayWithdrawEnabled;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  const isMaxRepayDebounced = t17;
  let t18;
  if ($[52] !== debouncedCollateral || $[53] !== debouncedLoan || $[54] !== isMaxRepayDebounced || $[55] !== marketParams || $[56] !== nativeAmount) {
    t18 = {
      marketParams,
      mode: "repayWithdraw",
      collateralAmount: debouncedCollateral,
      loanAmount: debouncedLoan,
      isMaxRepay: isMaxRepayDebounced,
      nativeAmount,
      withRequirements: false
    };
    $[52] = debouncedCollateral;
    $[53] = debouncedLoan;
    $[54] = isMaxRepayDebounced;
    $[55] = marketParams;
    $[56] = nativeAmount;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  const {
    buildError
  } = useMarketActionBuilder(t18);
  let t19;
  if ($[58] !== positionData) {
    t19 = positionData ? {
      collateral: positionData.collateral,
      borrowAssets: positionData.borrowAssets,
      price: positionData.market.price ?? ZERO
    } : undefined;
    $[58] = positionData;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  const current = t19;
  const isEmpty = inputCollateralAmount === ZERO && inputLoanAmount === ZERO;
  let t20;
  bb0: {
    if (isEmpty || !current) {
      let t21;
      if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
        t21 = {};
        $[60] = t21;
      } else {
        t21 = $[60];
      }
      t20 = t21;
      break bb0;
    }
    let t21;
    if ($[61] !== current || $[62] !== inputCollateralAmount || $[63] !== inputLoanAmount) {
      t21 = computePreviewFinalPosition({
        current,
        collateralDelta: inputCollateralAmount,
        loanDelta: inputLoanAmount,
        mode: "repayWithdraw"
      });
      $[61] = current;
      $[62] = inputCollateralAmount;
      $[63] = inputLoanAmount;
      $[64] = t21;
    } else {
      t21 = $[64];
    }
    t20 = t21;
  }
  const after = t20;
  let t21;
  if ($[65] !== buildError || $[66] !== debt || $[67] !== inputLoanAmount || $[68] !== isDebouncing || $[69] !== isEmpty || $[70] !== isRepayOverMax || $[71] !== isWithdrawOverMax || $[72] !== loanAsset?.symbol || $[73] !== wrapErrorMessage) {
    t21 = () => {
      if (wrapErrorMessage) {
        return wrapErrorMessage;
      }
      if (isEmpty || isDebouncing) {
        return;
      }
      const exceedsLoan = isRepayOverMax && isDefined(debt) && inputLoanAmount > debt;
      return getMarketActionError({
        buildError,
        insufficientBalanceSymbol: isRepayOverMax && !exceedsLoan ? loanAsset?.symbol : undefined,
        exceedsLoan,
        exceedsBorrowCapacity: isWithdrawOverMax
      });
    };
    $[65] = buildError;
    $[66] = debt;
    $[67] = inputLoanAmount;
    $[68] = isDebouncing;
    $[69] = isEmpty;
    $[70] = isRepayOverMax;
    $[71] = isWithdrawOverMax;
    $[72] = loanAsset?.symbol;
    $[73] = wrapErrorMessage;
    $[74] = t21;
  } else {
    t21 = $[74];
  }
  loanAsset?.symbol;
  let t22;
  if ($[75] !== t21) {
    t22 = t21();
    $[75] = t21;
    $[76] = t22;
  } else {
    t22 = $[76];
  }
  const buildErrorMessage = t22;
  const canTransferLoanAsset = useAssetCanTransferOnchain(account.address, loanAsset?.address, targetChain.id);
  const canTransferCollateralAsset = useAssetCanTransferOnchain(account.address, collateralAsset?.address, targetChain.id);
  const isLoanTransferBlocked = inputLoanAmount > ZERO && !canTransferLoanAsset;
  const isCollateralTransferBlocked = inputCollateralAmount > ZERO && !canTransferCollateralAsset;
  const {
    canTransfer: loanBundleAuthorized
  } = useBundleCanTransferOnchain("bundles.blueBundlesV1", loanAsset?.address, targetChain.id);
  let t23;
  if ($[77] !== inputCollateralAmount || $[78] !== inputLoanAmount || $[79] !== loanBundleAuthorized) {
    t23 = isMarketBundleBlocked({
      legs: [{
        amount: inputLoanAmount,
        authorized: loanBundleAuthorized
      }, {
        amount: inputCollateralAmount,
        authorized: true
      }]
    });
    $[77] = inputCollateralAmount;
    $[78] = inputLoanAmount;
    $[79] = loanBundleAuthorized;
    $[80] = t23;
  } else {
    t23 = $[80];
  }
  const isBundleNotAuthorized = t23;
  const isConfirmDisabled = !isMarketRepayWithdrawEnabled || isEmpty || isPositionLoading || !!positionError || inputLoanAmount > ZERO && isMaxRepayLoading || isMaxWithdrawLoading || account.isWrongChain || !!buildErrorMessage || isRepayOverMax || isWithdrawOverMax || isLoanTransferBlocked || isCollateralTransferBlocked || isBundleNotAuthorized;
  const tooltipContent = useMarketTooltip(buildErrorMessage);
  let t24;
  if ($[81] !== inputCollateralAmount || $[82] !== inputLoanAmount) {
    t24 = getRepayWithdrawOperationLabel(inputCollateralAmount, inputLoanAmount);
    $[81] = inputCollateralAmount;
    $[82] = inputLoanAmount;
    $[83] = t24;
  } else {
    t24 = $[83];
  }
  const operationLabel = t24;
  let t25;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = [];
    $[84] = t25;
  } else {
    t25 = $[84];
  }
  let t26;
  if ($[85] !== buildErrorMessage || $[86] !== inputCollateralAmount || $[87] !== inputLoanAmount || $[88] !== operationLabel) {
    t26 = {
      operationLabel,
      inputCollateralAmount,
      inputLoanAmount,
      isNew: true,
      localErrorMessage: buildErrorMessage,
      operations: t25,
      simulationError: undefined,
      blockError: null,
      isPending: false
    };
    $[85] = buildErrorMessage;
    $[86] = inputCollateralAmount;
    $[87] = inputLoanAmount;
    $[88] = operationLabel;
    $[89] = t26;
  } else {
    t26 = $[89];
  }
  const {
    confirmButtonLabel
  } = usePositionManagerMarketDialogLabels(t26);
  const isButtonLoading = account.isConnected && isPositionLoading;
  let t27;
  if ($[90] !== confirmButtonLabel || $[91] !== geoRestriction || $[92] !== isButtonLoading || $[93] !== isConfirmDisabled || $[94] !== tooltipContent) {
    t27 = getMarketGeoRestrictionActionProps({
      geoRestriction,
      isFinalizeDisabled: isConfirmDisabled,
      isLoading: isButtonLoading,
      finalizeLabel: confirmButtonLabel,
      tooltipContent
    });
    $[90] = confirmButtonLabel;
    $[91] = geoRestriction;
    $[92] = isButtonLoading;
    $[93] = isConfirmDisabled;
    $[94] = tooltipContent;
    $[95] = t27;
  } else {
    t27 = $[95];
  }
  let geoActionProps;
  let shouldBlockFinalize;
  if ($[96] !== t27) {
    ({
      shouldBlockFinalize,
      ...geoActionProps
    } = t27);
    $[96] = t27;
    $[97] = geoActionProps;
    $[98] = shouldBlockFinalize;
  } else {
    geoActionProps = $[97];
    shouldBlockFinalize = $[98];
  }
  let t28;
  if ($[99] !== collateralAsset || $[100] !== inputCollateralAmount || $[101] !== inputLoanAmount || $[102] !== isMaxRepay || $[103] !== loanAsset || $[104] !== market || $[105] !== marketId || $[106] !== marketParams || $[107] !== nativeAmount || $[108] !== shouldBlockFinalize || $[109] !== targetChain.id || $[110] !== targetChain.name || $[111] !== toggleDialog) {
    t28 = () => {
      if (!marketParams || !loanAsset || !collateralAsset || shouldBlockFinalize) {
        return;
      }
      trackMarketActionsV2EntryClicked({
        entryPoint: "position_manager",
        action: "repay_withdraw",
        marketId,
        chainId: targetChain.id,
        chainName: targetChain.name
      });
      toggleDialog(<MarketActionReviewDialogV2 marketParams={marketParams} marketId={marketId} mode="repayWithdraw" collateralAmount={inputCollateralAmount} loanAmount={inputLoanAmount} nativeAmount={nativeAmount} isMaxRepay={isMaxRepay} collateralAsset={collateralAsset} loanAsset={loanAsset} lltv={marketParams.lltv} marketForRewards={market} avgBorrowApy={market?.state.avgBorrowApy} onTxSuccess={() => {
        setInputCollateralAmount(ZERO);
        setInputLoanAmount(ZERO);
      }} entryPoint="position_manager" />, POSITION_MANAGER_DIALOG_OPTIONS);
    };
    $[99] = collateralAsset;
    $[100] = inputCollateralAmount;
    $[101] = inputLoanAmount;
    $[102] = isMaxRepay;
    $[103] = loanAsset;
    $[104] = market;
    $[105] = marketId;
    $[106] = marketParams;
    $[107] = nativeAmount;
    $[108] = shouldBlockFinalize;
    $[109] = targetChain.id;
    $[110] = targetChain.name;
    $[111] = toggleDialog;
    $[112] = t28;
  } else {
    t28 = $[112];
  }
  const handleFinalize = t28;
  if (isQueryLoading) {
    let t29;
    if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <PositionManagerRepayWithBalanceLoading />;
      $[113] = t29;
    } else {
      t29 = $[113];
    }
    return t29;
  }
  if (queryError || positionError && !positionData || !loanAsset || !collateralAsset) {
    let t29;
    if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <ErrorServiceUnavailable minHeight="400px" />;
      $[114] = t29;
    } else {
      t29 = $[114];
    }
    return t29;
  }
  const t29 = `Repay ${loanAsset.symbol} loan`;
  let t30;
  if ($[115] !== inputLoanAmount || $[116] !== isMaxRepayLoading || $[117] !== isRepayAtMax || $[118] !== loanAsset || $[119] !== maxRepay || $[120] !== onMaxRepayClick || $[121] !== t29) {
    t30 = <CompactAssetInput header={t29} amount={inputLoanAmount} token={loanAsset} onChange={setInputLoanAmount} onPendingChange={setIsLoanInputPending} maxValue={maxRepay} onMaxClick={onMaxRepayClick} isMax={isRepayAtMax} loading={isMaxRepayLoading} autoFocus={true} />;
    $[115] = inputLoanAmount;
    $[116] = isMaxRepayLoading;
    $[117] = isRepayAtMax;
    $[118] = loanAsset;
    $[119] = maxRepay;
    $[120] = onMaxRepayClick;
    $[121] = t29;
    $[122] = t30;
  } else {
    t30 = $[122];
  }
  let t31;
  if ($[123] !== isMarketRepayWithdrawEnabled || $[124] !== isWNativeLoan || $[125] !== isWrapping || $[126] !== isWrappingToggleLoading || $[127] !== loanAsset.symbol || $[128] !== onWrappingChange || $[129] !== targetChain.nativeCurrency.symbol || $[130] !== wrappableNativeBalance) {
    t31 = <WrapSegment id="position-manager-repay" enabled={isMarketRepayWithdrawEnabled} isWNative={isWNativeLoan} wrappableNativeBalance={wrappableNativeBalance} wrappedSymbol={loanAsset.symbol} nativeSymbol={targetChain.nativeCurrency.symbol} isWrapping={isWrapping} onWrappingChange={onWrappingChange} disabled={isWrappingToggleLoading} />;
    $[123] = isMarketRepayWithdrawEnabled;
    $[124] = isWNativeLoan;
    $[125] = isWrapping;
    $[126] = isWrappingToggleLoading;
    $[127] = loanAsset.symbol;
    $[128] = onWrappingChange;
    $[129] = targetChain.nativeCurrency.symbol;
    $[130] = wrappableNativeBalance;
    $[131] = t31;
  } else {
    t31 = $[131];
  }
  let t32;
  if ($[132] !== isLoanTransferBlocked || $[133] !== loanAsset.address) {
    t32 = isLoanTransferBlocked && <TransferError tokenAddress={loanAsset.address} />;
    $[132] = isLoanTransferBlocked;
    $[133] = loanAsset.address;
    $[134] = t32;
  } else {
    t32 = $[134];
  }
  let t33;
  if ($[135] !== t30 || $[136] !== t31 || $[137] !== t32) {
    t33 = <div className="flex flex-col gap-[2px]">{t30}{t31}{t32}</div>;
    $[135] = t30;
    $[136] = t31;
    $[137] = t32;
    $[138] = t33;
  } else {
    t33 = $[138];
  }
  const t34 = `Withdraw collateral ${collateralAsset.symbol}`;
  let t35;
  if ($[139] !== collateralAsset || $[140] !== inputCollateralAmount || $[141] !== isMaxWithdraw || $[142] !== isMaxWithdrawLoading || $[143] !== maxWithdraw || $[144] !== onMaxWithdrawClick || $[145] !== t34) {
    t35 = <CompactAssetInput header={t34} amount={inputCollateralAmount} token={collateralAsset} onChange={setInputCollateralAmount} onPendingChange={setIsCollateralInputPending} maxValue={maxWithdraw} onMaxClick={onMaxWithdrawClick} isMax={isMaxWithdraw} loading={isMaxWithdrawLoading} />;
    $[139] = collateralAsset;
    $[140] = inputCollateralAmount;
    $[141] = isMaxWithdraw;
    $[142] = isMaxWithdrawLoading;
    $[143] = maxWithdraw;
    $[144] = onMaxWithdrawClick;
    $[145] = t34;
    $[146] = t35;
  } else {
    t35 = $[146];
  }
  let t36;
  if ($[147] !== collateralAsset.address || $[148] !== isCollateralTransferBlocked) {
    t36 = isCollateralTransferBlocked && <TransferError tokenAddress={collateralAsset.address} />;
    $[147] = collateralAsset.address;
    $[148] = isCollateralTransferBlocked;
    $[149] = t36;
  } else {
    t36 = $[149];
  }
  let t37;
  if ($[150] !== t35 || $[151] !== t36) {
    t37 = <div className="flex flex-col gap-[2px]">{t35}{t36}</div>;
    $[150] = t35;
    $[151] = t36;
    $[152] = t37;
  } else {
    t37 = $[152];
  }
  const t38 = current?.collateral;
  const t39 = current?.borrowAssets;
  let t40;
  if ($[153] !== current) {
    t40 = current ? computeLtv({
      collateral: current.collateral,
      borrowAssets: current.borrowAssets,
      price: current.price
    }) : undefined;
    $[153] = current;
    $[154] = t40;
  } else {
    t40 = $[154];
  }
  let t41;
  if ($[155] !== t38 || $[156] !== t39 || $[157] !== t40) {
    t41 = {
      collateral: t38,
      loan: t39,
      ltv: t40
    };
    $[155] = t38;
    $[156] = t39;
    $[157] = t40;
    $[158] = t41;
  } else {
    t41 = $[158];
  }
  const t42 = marketParams?.lltv;
  const t43 = market?.state.avgBorrowApy;
  let t44;
  if ($[159] !== after || $[160] !== collateralAsset || $[161] !== isPositionLoading || $[162] !== loanAsset || $[163] !== market || $[164] !== marketId || $[165] !== t41 || $[166] !== t42 || $[167] !== t43) {
    t44 = <MarketSummaryCard marketId={marketId} current={t41} after={after} collateralAsset={collateralAsset} loanAsset={loanAsset} lltv={t42} isLoading={isPositionLoading} marketForRewards={market} avgBorrowApy={t43} background="muted" />;
    $[159] = after;
    $[160] = collateralAsset;
    $[161] = isPositionLoading;
    $[162] = loanAsset;
    $[163] = market;
    $[164] = marketId;
    $[165] = t41;
    $[166] = t42;
    $[167] = t43;
    $[168] = t44;
  } else {
    t44 = $[168];
  }
  let t45;
  if ($[169] !== isBundleNotAuthorized) {
    t45 = isBundleNotAuthorized && <BundleAuthorizationCallout variant="destructive" message={MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE} />;
    $[169] = isBundleNotAuthorized;
    $[170] = t45;
  } else {
    t45 = $[170];
  }
  const t46 = isCollateralInputPending || isLoanInputPending;
  let t47;
  if ($[171] !== geoActionProps || $[172] !== handleFinalize || $[173] !== isDebouncing || $[174] !== t46) {
    t47 = <ActionButtons handleFinalize={handleFinalize} {...geoActionProps} isInputPending={t46} isValidating={isDebouncing} />;
    $[171] = geoActionProps;
    $[172] = handleFinalize;
    $[173] = isDebouncing;
    $[174] = t46;
    $[175] = t47;
  } else {
    t47 = $[175];
  }
  let t48;
  if ($[176] !== t33 || $[177] !== t37 || $[178] !== t44 || $[179] !== t45 || $[180] !== t47) {
    t48 = <>{t33}{t37}{t44}{t45}{t47}</>;
    $[176] = t33;
    $[177] = t37;
    $[178] = t44;
    $[179] = t45;
    $[180] = t47;
    $[181] = t48;
  } else {
    t48 = $[181];
  }
  return t48;
};
export default PositionManagerRepayWithBalance;